import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import type { BimContractOtherCostData } from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import { TabsIdsDistribution } from 'pages/Project/Common/types';
import { cnMixSpace, Grid, Text } from 'uikit';

import cn from 'classnames';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BimEditOtherCommon } from './components/BimEditOtherCommon';
import { BimEditOtherLimits } from './components/BimEditOtherLimit';
import type { TEditOtherModalProps } from './types';

import Styles from './BimEditOtherModal.module.css';

const tabsItemsEdit = [
    { id: TabsIdsDistribution.COMMON, label: 'Общие данные' },
    { id: TabsIdsDistribution.LIMITS, label: 'Лимиты по договору' },
    { id: TabsIdsDistribution.FORMULA, label: 'Формула' },
];

const fieldsToCheck = {
    [TabsIdsDistribution.COMMON]: ['name', 'rationale', 'calculatingTypeValue'],
    [TabsIdsDistribution.LIMITS]: ['otherCostLimits'],
    [TabsIdsDistribution.FORMULA]: ['formula'],
};

const invisibleStyle = { visibility: 'hidden', position: 'absolute', overflow: 'hidden', maxHeight: 0 };

export const BimEditOtherModal = <T extends BimContractOtherCostData>(props: TEditOtherModalProps<T>) => {
    const {
        otherData,
        onSubmit,
        isDataLoading,
        isUpdateInProgress,
        statusWorkOrder,
        onCloseModal,
        constructionTypes,
        constructionTypeId,
        resetOtherData,
        forceUseRationaleField = false,
    } = props;

    const formRef = useRef<HTMLFormElement>(null);

    const { orderBim: orderBimRules } = useAccess();
    const [activeTab, setActiveTab] = useState(tabsItemsEdit[0]);

    const form = useForm({
        criteriaMode: 'all',
        shouldUnregister: true,
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
        setValue,
        watch,
    } = form;

    const resetDataDialog = useCallback(() => {
        onCloseModal();
        resetOtherData?.(undefined);
    }, [onCloseModal, resetOtherData]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: resetDataDialog,
        });

    const handleCloseModal = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            resetDataDialog();
        }
    }, [handleOpenConfirmationDialog, isDirty, resetDataDialog]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const createHandler = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const commonTabStyle = useMemo(
        () => (activeTab.id === TabsIdsDistribution.COMMON ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const limitsTabStyle = useMemo(
        () => (activeTab.id === TabsIdsDistribution.LIMITS ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const formulaTabStyle = useMemo(
        () => (activeTab.id === TabsIdsDistribution.FORMULA ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const isCalculationTypePercent = useMemo(
        () => otherData?.calculatingType === OtherCostCalculationTypeEnum.Percent,
        [otherData?.calculatingType],
    );

    const isFormulaFieldDisabled = useMemo(
        () => !isCalculationTypePercent || otherData?.isGlobalOtherCost,
        [isCalculationTypePercent, otherData?.isGlobalOtherCost],
    );

    const { deserializeFromBackend } = useEditorParseData({});

    useEffect(() => {
        if (otherData?.formula) {
            setValue('formula', deserializeFromBackend(otherData?.formula));
        }
    }, [otherData?.formula, deserializeFromBackend, setValue]);

    useEffect(
        () => () => {
            reset();
        },
        [reset],
    );

    return (
        <ModalDialog
            isOpen
            title="Редактирование прочих затрат"
            onConfirm={createHandler}
            onClose={handleCloseModal}
            btnConfirmLabel="Сохранить"
            btnConfirmDisabled={!isDirty || !orderBimRules?.canEdit}
            isLoading={isDataLoading}
            withTitleCloseButton
            isCommit={isUpdateInProgress}
            isOnClickOutsideDisable
        >
            <div className={cn(Styles.innerBlock, cnMixSpace({ p: 'l' }))}>
                <ControlledTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    items={tabsItemsEdit}
                    view="bordered"
                    size="s"
                    className={cnMixSpace({ mB: 'xl' })}
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                />
                <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                        <Grid style={commonTabStyle as CSSProperties}>
                            <BimEditOtherCommon
                                control={control}
                                otherData={otherData}
                                setValue={setValue}
                                statusWorkOrder={statusWorkOrder}
                                isTooltipHidden={!!commonTabStyle}
                                forceUseRationaleField={forceUseRationaleField}
                                orderBimRules={orderBimRules}
                            />
                        </Grid>
                        <Grid style={limitsTabStyle as CSSProperties}>
                            <BimEditOtherLimits
                                constructionTypes={constructionTypes}
                                control={control}
                                otherData={otherData}
                                constructionTypeId={constructionTypeId}
                                watch={watch}
                                areTooltipsHidden={!!limitsTabStyle}
                                orderBimRules={orderBimRules}
                            />
                        </Grid>

                        <Grid style={formulaTabStyle as CSSProperties}>
                            {isCalculationTypePercent ? (
                                <FormulaEditor
                                    name="formula"
                                    control={control}
                                    getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                                    isDisabled={isFormulaFieldDisabled}
                                    tooltipTitle="Формула расчета"
                                    rules={{
                                        required: !isFormulaFieldDisabled && 'Обязательное поле',
                                    }}
                                    isTooltipHidden={!!formulaTabStyle}
                                />
                            ) : (
                                <Text view="secondary" as="span" className={Styles.formulaUnavailableText}>
                                    Для способа расчета &quot;Расчет&quot; ввод формул недоступен
                                </Text>
                            )}
                        </Grid>
                    </form>
                </FormProvider>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

BimEditOtherModal.displayName = 'BimEditOtherModal';
